import { Button, ProgressHeader, Typography } from '@/components';
import { PencilIcon } from '@/components/atoms';
import { CameraIcon, CheckIcon, IdCardIcon, RefreshIcon } from '@/components/atoms/icons';
import XIcon from '@/components/atoms/icons/XIcon';
import { OkDialog } from '@/components/molecules/dialogs/OkDialog';
import { useTranslation, useUIColors } from '@/core/hooks';
import { logger } from '@/core/logger';
import { trackDynatraceEvent } from '@/core/services/dynatraceService';
import { asyncStorage } from '@/core/storage';
import { FlatTranslationKey } from '@/core/types/i18n';
import { useDocumentUpload } from '@/modules/onboarding/identity/document-upload/hooks/useDocumentUpload';
import { REGISTRATION_STEP, REGISTRATION_TYPE, useManualRegistrationStore } from '@/modules/onboarding/registration/store/registrationStore';
import { calculateRegistrationProgress, getRegistrationFlow, useRegistrationProgress } from '@/modules/onboarding/registration/utils/registrationProgress';
import ImageSvg from '@assets/images/svg/image.svg';
import * as FileSystem from 'expo-file-system';
import * as FileSystemLegacy from 'expo-file-system/legacy';
import { router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Image, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const DOCUMENT_STORAGE_KEY = 'document.images';

interface DocumentImages {
    emiratesIdFront?: string;
    emiratesIdBack?: string;
    passport?: string;
}

type DocumentType = 'emiratesIdFront' | 'emiratesIdBack' | 'passport';

const PREVIEW_CARD_MAX_WIDTH = 300;
const PREVIEW_IMAGE_HEIGHT = 169.139;
const PREVIEW_IMAGE_ASPECT_RATIO = PREVIEW_CARD_MAX_WIDTH / PREVIEW_IMAGE_HEIGHT;
const PREVIEW_CARD_RADIUS = 16;

export function DocumentUploadScreen() {
    const { t } = useTranslation();
    const colors = useUIColors();
    const insets = useSafeAreaInsets();
    const params = useLocalSearchParams();
    const registrationStore = useManualRegistrationStore();
    const baseProgress = useRegistrationProgress();

    // Check if opened in preview mode (from document review screen)
    const isPreviewMode = params.previewMode === 'true' || params.hideConfirm === 'true';
    
    // Get the specific document type to preview (if in preview mode)
    const previewDocumentType = params.previewDocumentType as DocumentType | undefined;

    // Get registration type to determine required documents
    // Allow override from params for passport-only flow after EID completion
    const registrationType = (params.registrationType as 'emirates-id' | 'passport') || registrationStore.registrationType;
    const registrationResponse = registrationStore.registrationResponse;

    // Track page open view for document upload
    useEffect(() => {
        trackDynatraceEvent({
            p_screen: 'documentUpload',
            p_actionName: 'Open Document Upload Screen',
            p_sectionName: registrationType,
            eventType: 'view',
        });
    }, [registrationType]);

    // Derived progress: match document review progress when in preview mode
    const progress = useMemo(() => {
        if (!isPreviewMode) {
            return baseProgress;
        }
        const flow = getRegistrationFlow(registrationType || null);
        // Use document-review route to mirror review screen progress
        return calculateRegistrationProgress('/document-review', flow, undefined);
    }, [baseProgress, isPreviewMode, registrationType]);

    // Document upload hook
    const {
        uploadStatus,
        uploadDocument: uploadDocumentToApi,
        retryUpload,
        resetUploadStatus,
        clearUploadStatus,
        isUploading: hookIsUploading,
        isInitialized: isUploadStatusInitialized,
    } = useDocumentUpload(registrationResponse);

    // Compute isUploading reactively from uploadStatus
    // This ensures it updates when uploadStatus changes
    const isUploading = useMemo(() => {
        const uploading = Object.values(uploadStatus).some(
            (status) => status?.status === 'uploading'
        );
        logger.debug('DocumentUploadScreen', 'Computed isUploading', {
            uploading,
            uploadStatuses: Object.entries(uploadStatus).map(([key, value]) => ({
                documentType: key,
                status: value?.status,
            })),
        });
        return uploading;
    }, [uploadStatus]);

    // State for document images
    const [documents, setDocuments] = useState<DocumentImages>({});
    // State to force image reload when recapturing (cache busting)
    const [imageKeys, setImageKeys] = useState<Record<DocumentType, number>>({
        emiratesIdFront: 0,
        emiratesIdBack: 0,
        passport: 0,
    });
    // Ref to track which documents are currently being uploaded to prevent duplicate uploads
    const uploadingRef = useRef<Set<DocumentType>>(new Set());

    const saveDocuments = useCallback(async (docs: DocumentImages) => {
        try {
            await asyncStorage.setItem(DOCUMENT_STORAGE_KEY, JSON.stringify(docs));
        } catch (error) {
            logger.error('DocumentUploadScreen', 'Failed to save documents', error);
        }
    }, []);

    const [errorMessage, setErrorMessage] = useState<string>('');
    const [errorVisible, setErrorVisible] = useState(false);

    const showError = useCallback((message?: string) => {
        setErrorMessage(message || 'Something went wrong. Please try again.');
        setErrorVisible(true);
    }, []);

    // Watch for upload errors surfaced in uploadStatus
    useEffect(() => {
        const firstError = Object.values(uploadStatus).find((status) => status?.status === 'error' && (status as any).error);
        if (firstError && (firstError as any).error) {
            showError((firstError as any).error as string);
        }
    }, [uploadStatus, showError]);

    const loadDocuments = useCallback(async () => {
        try {
            // Wait for upload status to be initialized before checking upload status
            // This prevents race condition where we check status before it's loaded from storage
            if (!isUploadStatusInitialized) {
                logger.debug('DocumentUploadScreen', 'Upload status not initialized yet, skipping upload checks');
                return;
            }

            const stored = await asyncStorage.getItem(DOCUMENT_STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored) as DocumentImages;
                // Verify that stored file URIs still exist
                const verifiedDocuments: DocumentImages = {};
                for (const [key, uri] of Object.entries(parsed)) {
                    if (uri) {
                        try {
                            // Check if file exists (for file:// URIs)
                            const cacheDir = (FileSystem as any).cacheDirectory as string | undefined;
                            if (uri.startsWith('file://') || (cacheDir && uri.startsWith(cacheDir))) {
                                const fileInfo = await FileSystemLegacy.getInfoAsync(uri);
                                if (fileInfo.exists) {
                                    verifiedDocuments[key as DocumentType] = uri;
                                } else {
                                    logger.warn('DocumentUploadScreen', `Stored file does not exist: ${uri}`);
                                }
                            } else {
                                // For non-file URIs (e.g., remote URLs), keep them
                                verifiedDocuments[key as DocumentType] = uri;
                            }
                        } catch (error) {
                            logger.error('DocumentUploadScreen', `Failed to verify file: ${uri}`, error);
                            showError();
                            // Skip this document if verification fails
                        }
                    }
                }
                // Always update state, even if values appear the same, to force re-render
                // This ensures recaptured images (same URI but new file content) are displayed
                setDocuments(prevDocuments => {
                    // Compare to detect actual changes
                    const hasChanges = Object.keys(verifiedDocuments).length !== Object.keys(prevDocuments).length ||
                        Object.keys(verifiedDocuments).some(key => verifiedDocuments[key as DocumentType] !== prevDocuments[key as DocumentType]);

                    if (hasChanges) {
                        logger.info('DocumentUploadScreen', 'Documents updated', {
                            keys: Object.keys(verifiedDocuments),
                            prevKeys: Object.keys(prevDocuments)
                        });

                        // Detect newly added documents and trigger upload
                        // In preview mode, completely skip all upload logic (preview only, no uploads)
                        if (isPreviewMode) {
                            logger.debug('DocumentUploadScreen', 'Preview mode: Skipping all upload logic', {
                                verifiedDocuments: Object.keys(verifiedDocuments),
                            });
                            // In preview mode, just return the documents without any upload logic
                            // Still update image keys for display
                            setImageKeys(prevKeys => {
                                const newKeys = { ...prevKeys };
                                Object.keys(verifiedDocuments).forEach(key => {
                                    if (verifiedDocuments[key as DocumentType] !== prevDocuments[key as DocumentType]) {
                                        newKeys[key as DocumentType] = (newKeys[key as DocumentType] || 0) + 1;
                                    }
                                });
                                return newKeys;
                            });
                            return verifiedDocuments;
                        }

                        Object.keys(verifiedDocuments).forEach(key => {
                            const docType = key as DocumentType;
                            const newUri = verifiedDocuments[docType];
                            const prevUri = prevDocuments[docType];

                            // If this is a new document (wasn't there before) or was recaptured
                            if (newUri && registrationResponse) {
                                const uploadState = uploadStatus[docType];
                                const currentStatus = uploadState?.status;
                                const isUploading = currentStatus === 'uploading';
                                const isIdle = currentStatus === 'idle';
                                const isUploaded = currentStatus === 'uploaded';

                                // Skip if currently uploading
                                if (isUploading) {
                                    return;
                                }

                                // CRITICAL: Check upload status FIRST before any other logic
                                // If document is already uploaded, skip upload regardless of URI comparison
                                // This prevents re-upload when navigating between views or on screen remount
                                if (isUploaded) {
                                    logger.debug('DocumentUploadScreen', 'Document already uploaded, skipping upload', {
                                        documentType: docType,
                                        uri: newUri,
                                        currentStatus,
                                        prevUri,
                                        uriMatches: newUri === prevUri,
                                    });
                                    return;
                                }

                                // If URI changed, it's a new/recaptured document - always upload
                                if (newUri !== prevUri) {
                                    // Check if already uploading to prevent duplicate uploads
                                    if (uploadingRef.current.has(docType)) {
                                        logger.debug('DocumentUploadScreen', 'Upload already in progress, skipping duplicate', {
                                            documentType: docType,
                                            newUri,
                                            prevUri,
                                        });
                                        return;
                                    }

                                    logger.info('DocumentUploadScreen', 'New/recaptured document detected (URI changed), triggering upload', {
                                        documentType: docType,
                                        newUri,
                                        prevUri,
                                        isPreviewMode,
                                        hasRegistrationResponse: !!registrationResponse,
                                    });

                                    // Mark as uploading to prevent duplicates
                                    uploadingRef.current.add(docType);

                                    // Reset upload status to allow re-upload of recaptured document
                                    resetUploadStatus(docType);

                                    // Trigger upload for new/recaptured document
                                    uploadDocumentToApi(docType, newUri)
                                        .then(() => {
                                            // Remove from uploading set on success
                                            uploadingRef.current.delete(docType);
                                        })
                                        .catch((error) => {
                                            // Remove from uploading set on error
                                            uploadingRef.current.delete(docType);
                                            logger.error('DocumentUploadScreen', 'Failed to upload document from loadDocuments', {
                                                documentType: docType,
                                                error,
                                            });
                                        });
                                } else if (newUri && !prevUri) {
                                    // Document was just added (wasn't there before)
                                    // This case should already be handled by the isUploaded check above
                                    // But add extra safety check
                                    if (isUploaded) {
                                        logger.debug('DocumentUploadScreen', 'Document already uploaded (new in state but uploaded), skipping upload', {
                                            documentType: docType,
                                            uri: newUri,
                                            currentStatus,
                                        });
                                        return;
                                    }
                                    
                                    // Check if already uploading to prevent duplicate uploads
                                    if (uploadingRef.current.has(docType)) {
                                        logger.debug('DocumentUploadScreen', 'Upload already in progress, skipping duplicate', {
                                            documentType: docType,
                                            uri: newUri,
                                        });
                                        return;
                                    }

                                    logger.info('DocumentUploadScreen', 'New document detected, triggering upload', {
                                        documentType: docType,
                                        uri: newUri,
                                        hasRegistrationResponse: !!registrationResponse,
                                    });

                                    // Mark as uploading to prevent duplicates
                                    uploadingRef.current.add(docType);

                                    // Trigger upload for new document
                                    uploadDocumentToApi(docType, newUri)
                                        .then(() => {
                                            // Remove from uploading set on success
                                            uploadingRef.current.delete(docType);
                                        })
                                        .catch((error) => {
                                            // Remove from uploading set on error
                                            uploadingRef.current.delete(docType);
                                            logger.error('DocumentUploadScreen', 'Failed to upload document from loadDocuments', {
                                                documentType: docType,
                                                error,
                                            });
                                        });
                                } else if (newUri && prevUri && newUri === prevUri) {
                                    // Same URI - could be a recapture
                                    // Check if status is 'idle' (was reset when user clicked edit)
                                    if (isIdle) {
                                        // Check if already uploading to prevent duplicate uploads
                                        if (uploadingRef.current.has(docType)) {
                                            logger.debug('DocumentUploadScreen', 'Upload already in progress, skipping duplicate', {
                                                documentType: docType,
                                                uri: newUri,
                                            });
                                            return;
                                        }

                                        logger.info('DocumentUploadScreen', 'Document recaptured (same URI, status reset to idle), triggering upload', {
                                            documentType: docType,
                                            uri: newUri,
                                            currentStatus,
                                            isPreviewMode,
                                        });

                                        // Mark as uploading to prevent duplicates
                                        uploadingRef.current.add(docType);

                                        // Trigger upload for recaptured document (same URI but status was reset)
                                        uploadDocumentToApi(docType, newUri)
                                            .then(() => {
                                                // Remove from uploading set on success
                                                uploadingRef.current.delete(docType);
                                            })
                                            .catch((error) => {
                                                // Remove from uploading set on error
                                                uploadingRef.current.delete(docType);
                                                logger.error('DocumentUploadScreen', 'Failed to upload recaptured document from loadDocuments', {
                                                    documentType: docType,
                                                    error,
                                                });
                                            });
                                    } else {
                                        // Same URI and not idle - document exists but no upload needed
                                        logger.debug('DocumentUploadScreen', 'Document exists with same URI, no upload needed', {
                                            documentType: docType,
                                            uri: newUri,
                                            currentStatus,
                                        });
                                    }
                                }
                            }
                        });

                        // Increment image key for changed documents to force reload
                        setImageKeys(prevKeys => {
                            const newKeys = { ...prevKeys };
                            Object.keys(verifiedDocuments).forEach(key => {
                                if (verifiedDocuments[key as DocumentType] !== prevDocuments[key as DocumentType]) {
                                    newKeys[key as DocumentType] = (newKeys[key as DocumentType] || 0) + 1;
                                }
                            });
                            return newKeys;
                        });
                        return verifiedDocuments;
                    }
                    // No changes detected, but still return new object reference for React
                    return { ...verifiedDocuments };
                });

                // REMOVED: setTimeout upload trigger to prevent duplicate uploads
                // Uploads are now handled in the setDocuments callback above
                // The setTimeout was causing race conditions where multiple uploads were triggered
                // when loadDocuments() was called multiple times (from focus effect, status initialization, etc.)

                // Update storage with verified documents (remove missing files)
                if (Object.keys(verifiedDocuments).length !== Object.keys(parsed).length) {
                    await saveDocuments(verifiedDocuments);
                }
            } else {
                // If no stored documents, clear state
                setDocuments({});
            }
        } catch (error) {
            logger.error('DocumentUploadScreen', 'Failed to load documents', error);
        }
    }, [saveDocuments, registrationResponse, uploadDocumentToApi, uploadStatus, resetUploadStatus, isUploadStatusInitialized, isPreviewMode]);

    // Load documents from storage on mount
    useEffect(() => {
        loadDocuments();
    }, [loadDocuments]);

    // Reload documents when upload status is initialized (to check upload status properly)
    useEffect(() => {
        if (isUploadStatusInitialized) {
            logger.debug('DocumentUploadScreen', 'Upload status initialized, reloading documents to check upload status');
            loadDocuments();
        }
    }, [isUploadStatusInitialized, loadDocuments]);

    // Reload documents when screen comes into focus to ensure UI is in sync
    useFocusEffect(
        useCallback(() => {
            loadDocuments();
            // Increment image keys to force reload when coming back from scanner
            // This ensures recaptured images are displayed even if URI is the same
            setImageKeys(prevKeys => {
                const newKeys = { ...prevKeys };
                Object.keys(newKeys).forEach(key => {
                    newKeys[key as DocumentType] = (newKeys[key as DocumentType] || 0) + 1;
                });
                logger.info('DocumentUploadScreen', 'Incremented image keys on focus', { keys: newKeys });
                return newKeys;
            });
        }, [loadDocuments])
    );

    // Handle incoming image from document scanner
    // Allow processing even in preview mode if we're coming back from editing (has croppedImage)
    useEffect(() => {
        const capturedImage = params.croppedImage as string | undefined;
        const incomingDocumentType = params.documentType as DocumentType | undefined;

        // If in preview mode and no new image captured, skip processing (initial preview load)
        // But if we have a new image (coming back from edit), process it
        if (isPreviewMode && !capturedImage) {
            return;
        }

        if (capturedImage && incomingDocumentType) {
            logger.info('DocumentUploadScreen', 'Processing captured document', {
                documentType: incomingDocumentType,
                isPreviewMode,
                hasRegistrationResponse: !!registrationResponse,
                registrationResponse: registrationResponse ? {
                    customer_id: registrationResponse.customer_id,
                    registration_id: registrationResponse.registration_id,
                    documentsCount: registrationResponse.documents?.length || 0,
                } : null,
            });

            // Use functional update to ensure we have the latest state
            setDocuments((prevDocuments) => {
                const updatedDocuments = { ...prevDocuments, [incomingDocumentType]: capturedImage };
                // Save to storage asynchronously
                saveDocuments(updatedDocuments).catch((error: unknown) => {
                    logger.error('DocumentUploadScreen', 'Failed to save documents after capture', error);
                });
                logger.info('DocumentUploadScreen', `Document captured: ${incomingDocumentType}`, { uri: capturedImage });

                return updatedDocuments;
            });

            // Upload document immediately after capture (even in preview mode when editing)
            // This ensures edited documents from preview are uploaded
            // Check upload status first to prevent duplicate uploads
            if (registrationResponse) {
                const uploadState = uploadStatus[incomingDocumentType];
                const currentStatus = uploadState?.status;
                const isUploaded = currentStatus === 'uploaded';
                const isUploading = currentStatus === 'uploading';

                // Skip upload if already uploaded or currently uploading
                if (isUploaded || isUploading) {
                    logger.debug('DocumentUploadScreen', 'Skipping upload from params - document already uploaded or uploading', {
                        documentType: incomingDocumentType,
                        currentStatus,
                        isPreviewMode,
                    });
                } else {
                    logger.info('DocumentUploadScreen', 'Starting document upload', {
                        documentType: incomingDocumentType,
                        isPreviewMode,
                        customerId: registrationResponse.customer_id,
                        registrationId: registrationResponse.registration_id,
                    });
                    uploadDocumentToApi(incomingDocumentType, capturedImage).catch((error) => {
                        logger.error('DocumentUploadScreen', 'Failed to upload document after capture', {
                            documentType: incomingDocumentType,
                            error,
                        });
                    });
                }
            } else {
                logger.warn('DocumentUploadScreen', 'Cannot upload document - registration response not available', {
                    documentType: incomingDocumentType,
                });
            }

            // Clear params to prevent re-processing
            router.setParams({ croppedImage: undefined, documentType: undefined } as any);
        }

    }, [params.croppedImage, params.documentType, saveDocuments, registrationResponse, uploadDocumentToApi, uploadStatus, isPreviewMode]);


    const handleDocumentAction = (documentType: DocumentType) => {
        // If editing an already uploaded document, reset upload status to allow re-upload
        const currentStatus = uploadStatus[documentType]?.status;
        if (currentStatus === 'uploaded') {
            logger.info('DocumentUploadScreen', 'Resetting upload status for edit/recapture', {
                documentType,
                currentStatus,
            });
            resetUploadStatus(documentType);
        }

        trackDynatraceEvent({
            p_screen: 'documentUpload',
            p_actionName: `Open Document Scanner - ${documentType}`,
            documentType,
            eventType: 'action',
        });

        // Navigate to scanner (for both capture and edit)
        // Preserve preview mode params if in preview mode (for editing from preview)
        router.push({
            pathname: '/document-scanner',
            params: {
                documentType,
                // Preserve preview mode params if they exist
                ...(isPreviewMode && {
                    previewMode: 'true',
                    hideConfirm: 'true',
                    ...(previewDocumentType && { previewDocumentType })
                })
            }
        } as any);
    };

    const handleClearDocument = async (documentType: DocumentType) => {
        const imageUri = documents[documentType];

        // Delete the image file from cache if it exists
        if (imageUri) {
            try {
                // Check if it's a file URI (starts with file:// or is in cache directory)
                const isFileUri = imageUri.startsWith('file://');
                const cacheDir = (FileSystem as any).cacheDirectory as string | undefined;
                const isCacheFile = cacheDir && imageUri.startsWith(cacheDir);

                if (isFileUri || isCacheFile) {
                    const fileInfo = await FileSystem.getInfoAsync(imageUri);
                    if (fileInfo.exists) {
                        await FileSystem.deleteAsync(imageUri, { idempotent: true });
                        logger.info('DocumentUploadScreen', `Deleted image file: ${imageUri}`);
                    } else {
                        logger.warn('DocumentUploadScreen', `File does not exist: ${imageUri}`);
                    }
                }
            } catch (error) {
                logger.error('DocumentUploadScreen', `Failed to delete image file: ${imageUri}`, error);
                // Continue with state update even if file deletion fails
            }
        }

        // Clear upload status when document is cleared
        clearUploadStatus(documentType);

        // Update state and storage
        setDocuments((prevDocuments) => {
            const updatedDocuments = { ...prevDocuments };
            delete updatedDocuments[documentType];
            // Save to storage asynchronously
            saveDocuments(updatedDocuments).catch((error: unknown) => {
                logger.error('DocumentUploadScreen', 'Failed to save documents after clear', error);
            });
            logger.info('DocumentUploadScreen', `Document cleared: ${documentType}`);
            return updatedDocuments;
        });
    };

    // Determine which documents are required based on registration type
    const getRequiredDocuments = (): DocumentType[] => {
        if (registrationType === REGISTRATION_TYPE.EMIRATES_ID) {
            // UAE citizen/resident: Emirates ID (front + back) only
            // Passport will be captured in a separate flow after EID is complete
            return ['emiratesIdFront', 'emiratesIdBack'];
        } else {
            // International: Only passport
            return ['passport'];
        }
    };

    // Check if all required documents are captured AND uploaded
    const areAllRequiredDocumentsCaptured = (): boolean => {
        const required = getRequiredDocuments();
        return required.every((docType) => {
            const hasDocument = !!documents[docType];
            const uploadState = uploadStatus[docType];
            const isUploaded = uploadState?.status === 'uploaded';
            return hasDocument && isUploaded;
        });
    };

    const handleContinue = () => {
        trackDynatraceEvent({
            p_screen: 'documentUpload',
            p_actionName: `Continue Document Upload${previewDocumentType ? ` - ${previewDocumentType}` : ''}`,
            documentType: previewDocumentType || (registrationType === 'passport' ? 'passport' : 'emiratesId'),
            eventType: 'action',
        });

        // If in preview mode (opened from document review), just go back
        if (isPreviewMode) {
            logger.info('DocumentUploadScreen', 'Preview mode: going back to document review');
            router.back();
            return;
        }

        if (!areAllRequiredDocumentsCaptured()) {
            return;
        }

        // For UAE users: After EID front+back are complete, navigate to passport capture
        if (registrationType === REGISTRATION_TYPE.EMIRATES_ID) {
            const eidFrontUploaded = uploadStatus.emiratesIdFront?.status === 'uploaded';
            const eidBackUploaded = uploadStatus.emiratesIdBack?.status === 'uploaded';
            const passportUploaded = uploadStatus.passport?.status === 'uploaded';

            // If EID is complete but passport is not, navigate to passport capture
            if (eidFrontUploaded && eidBackUploaded && !passportUploaded) {
                // Update step to document-scanner (passport) before navigating
                registrationStore.setStep(REGISTRATION_STEP.DOCUMENT_CAPTURE);
                logger.info('DocumentUploadScreen', 'EID documents complete, updating step to passport capture', {
                    eidFrontUploaded,
                    eidBackUploaded,
                    passportUploaded,
                });
                // Navigate to passport-only document viewer
                router.push({
                    pathname: '/document-upload',
                    params: { registrationType: 'passport' } as any,
                });
                return;
            }

            // If all documents (including passport) are complete, go to document review
            if (eidFrontUploaded && eidBackUploaded && passportUploaded) {
                // Update step to document review before navigating
                registrationStore.setStep(REGISTRATION_STEP.DOCUMENT_REVIEW);
                logger.info('DocumentUploadScreen', 'All documents complete, updating step to document review', {
                    eidFrontUploaded,
                    eidBackUploaded,
                    passportUploaded,
                });
                router.push('/document-review');
                return;
            }
        }

        // For international users or if all documents are complete, navigate to document review
        registrationStore.setStep(REGISTRATION_STEP.DOCUMENT_REVIEW);
        logger.info('DocumentUploadScreen', 'All documents complete, updating step to document review');
        router.push('/document-review');
    };

    const DocumentCard = ({
        documentType,
        label,
        sideLabel
    }: {
        documentType: DocumentType;
        label: string;
        sideLabel: string;
    }) => {
        const imageWidth = PREVIEW_CARD_MAX_WIDTH;
        const imageHeight = PREVIEW_IMAGE_HEIGHT;
        const imageUri = documents[documentType];
        const hasImage = !!imageUri;
        const uploadState = uploadStatus[documentType];
        const isUploading = uploadState?.status === 'uploading';
        const isUploaded = uploadState?.status === 'uploaded';
        const hasError = uploadState?.status === 'error';
        
        // State to control showing uploaded overlay (auto-hide after 2 seconds)
        const [showUploadedOverlay, setShowUploadedOverlay] = React.useState(false);

        // Show uploaded overlay when upload completes, then hide after 2 seconds
        React.useEffect(() => {
            if (isUploaded) {
                setShowUploadedOverlay(true);
                const timer = setTimeout(() => {
                    setShowUploadedOverlay(false);
                }, 2000); // Hide after 2 seconds
                return () => clearTimeout(timer);
            } else {
                setShowUploadedOverlay(false);
                return undefined;
            }
        }, [isUploaded]);

        return (
            <View style={[styles.documentCard, isPreviewMode && styles.previewCard]}>
                <View style={[styles.documentImageContainer, isPreviewMode && styles.previewImageContainer]}>
                    {hasImage ? (
                        <View style={[styles.imageWrapper, isPreviewMode && styles.previewImageWrapper, isPreviewMode && { width: imageWidth }]}>
                            <View style={[
                                styles.imageContainer,
                                isPreviewMode && { width: imageWidth, height: imageHeight, borderRadius: PREVIEW_CARD_RADIUS }
                            ]}>
                                <Image
                                    key={`${imageUri}-${imageKeys[documentType]}`}
                                    source={{ uri: `${imageUri}?t=${imageKeys[documentType]}` }}
                                    style={[
                                        styles.documentImage,
                                        isPreviewMode && { width: imageWidth, height: imageHeight, borderRadius: PREVIEW_CARD_RADIUS }
                                    ]}
                                    resizeMode="cover"
                                />
                                {/* Upload status overlay */}
                                {/* Show overlay for: uploading, uploaded (for 2 seconds), or error (with retry) */}
                                {(isUploading || showUploadedOverlay || hasError) && (
                                    <View style={styles.uploadStatusOverlay}>
                                        {isUploading && (
                                            <View style={styles.uploadStatusContent}>
                                                <ActivityIndicator size="small" color={colors.white} />
                                                <Typography variant="footnote" color="white" style={styles.uploadStatusText}>
                                                    {t('documentViewer.uploading')}
                                                </Typography>
                                            </View>
                                        )}
                                        {showUploadedOverlay && !isUploading && (
                                            <View style={styles.uploadStatusContent}>
                                                <View style={[styles.checkmarkCircle, { backgroundColor: colors.outlineSuccess }]}>
                                                    <CheckIcon size={14} color="white" />
                                                </View>
                                                <Typography variant="footnote" color="white" style={styles.uploadStatusText}>
                                                    {t('documentViewer.uploaded')}
                                                </Typography>
                                            </View>
                                        )}
                                        {hasError && !isUploading && !showUploadedOverlay && (
                                            <View style={styles.uploadStatusContent}>
                                                <View style={[styles.errorCircle, { backgroundColor: colors.outlineError }]}>
                                                    <XIcon width={14} height={14} color="white" />
                                                </View>
                                                <Typography variant="footnote" color="white" style={styles.uploadStatusText}>
                                                    {t('documentViewer.uploadError')}
                                                </Typography>
                                                <Button
                                                    variant="text"
                                                    theme='dark'
                                                    title={t('common.retry')}
                                                    onPress={() => imageUri && retryUpload(documentType, imageUri)}
                                                    leading={<RefreshIcon size={18} color="white" />}
                                                    style={styles.retryButton}
                                                />
                                            </View>
                                        )}
                                    </View>
                                )}
                            </View>
                            <View style={[styles.imageActions, isPreviewMode && styles.previewImageActions]}>
                                {isPreviewMode ? (
                                    <>
                                        <Typography variant="headline" color="primary" style={styles.documentLabel}>
                                            {label}
                                        </Typography>
                                        <Pressable
                                            onPress={() => handleDocumentAction(documentType)}
                                            disabled={isUploading}
                                            style={styles.editLink}
                                            accessibilityRole="button"
                                        >
                                            <PencilIcon size={18} color={colors.fontPrimary} />
                                            <Typography
                                                variant="headline"
                                                color="primary"
                                                style={styles.editLinkText}
                                            >
                                                {t('documentViewer.edit')}
                                            </Typography>
                                        </Pressable>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            variant="text"
                                            title={t('documentViewer.edit')}
                                            onPress={() => handleDocumentAction(documentType)}
                                            leading={<PencilIcon size={18} color={colors.fontPrimary} />}
                                            style={styles.imageActionButton}
                                            disabled={isUploading}
                                        />
                                        <Button
                                            variant="text"
                                            title={t('documentViewer.clear')}
                                            onPress={() => handleClearDocument(documentType)}
                                            leading={<XIcon width={18} height={18} color={colors.fontPrimary} />}
                                            style={styles.imageActionButton}
                                            disabled={isUploading}
                                        />
                                    </>
                                )}
                            </View>
                        </View>
                    ) : (
                        <Pressable
                            style={[styles.emptyStateContainer, isPreviewMode && styles.previewImageWrapper]}
                            onPress={() => handleDocumentAction(documentType)}
                        >
                            <View style={styles.emptyStateBorder}>
                                <View style={styles.emptyStateContent}>
                                    <View style={styles.placeholderIconContainer}>
                                        <ImageSvg width={39.169} height={39.169} />
                                    </View>
                                    <Typography variant="headline" color="secondary" style={styles.emptyStateText}>
                                        {sideLabel}
                                    </Typography>
                                    <Button
                                        variant="text"
                                        title={t('documentViewer.capture')}
                                        onPress={() => handleDocumentAction(documentType)}
                                        leading={<CameraIcon size={18} color={colors.fontPrimary} />}
                                        style={styles.uploadButtonInside}
                                    />
                                </View>
                            </View>
                        </Pressable>
                    )}
                </View>
            </View>
        );
    };

    return (
        <View style={styles.container}>
            {/* Header */}
            <ProgressHeader
                progress={progress}
                showBackButton={isPreviewMode ? true : false}
                disableBack={isUploading}
            />

            {/* Main Content */}
            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={[
                    styles.scrollContent,
                    {
                        paddingTop: isPreviewMode ? 32 : 16,
                        paddingBottom: registrationType === REGISTRATION_TYPE.EMIRATES_ID ? 250 : 200,
                        alignItems: 'center',
                    }
                ]}
                showsVerticalScrollIndicator={false}
            >
                {/* Page Header */}
                <View style={[styles.pageHeader, isPreviewMode && styles.previewPageHeader]}>
                    <View style={styles.iconContainer}>
                        <IdCardIcon size={40} />
                    </View>
                    <Typography variant="title1" color="primary" style={styles.pageTitle}>
                        {(() => {
                            // In preview mode, show specific preview titles from Figma
                            if (isPreviewMode) {
                                const previewTitleKey = (
                                    previewDocumentType === 'passport'
                                        ? 'documentViewer.previewTitlePassport'
                                        : 'documentViewer.previewTitleEmiratesId'
                                ) as FlatTranslationKey;
                                return t(previewTitleKey);
                            }
                            return registrationType === REGISTRATION_TYPE.EMIRATES_ID
                                ? t('documentViewer.uploadTitle')
                                : t('documentViewer.uploadTitlePassport');
                        })()}
                    </Typography>
                    {!isPreviewMode && (
                        <>
                            <Typography variant="body" color="primary" style={styles.pageSubtitle}>
                                {registrationType === REGISTRATION_TYPE.EMIRATES_ID
                                    ? t('documentViewer.uploadSubtitle')
                                    : t('documentViewer.uploadSubtitlePassport')}
                            </Typography>
                            <View style={styles.noteContainer}>
                                <Typography variant="body" color="primary" style={styles.noteText}>
                                    <Typography variant="headline" color="primary" style={styles.noteBold}>
                                        {t('documentViewer.note')}
                                    </Typography>
                                    {': '}
                                    {registrationType === REGISTRATION_TYPE.EMIRATES_ID
                                        ? t('documentViewer.noteText')
                                        : t('documentViewer.noteTextPassport')}
                                </Typography>
                            </View>
                        </>
                    )}
                </View>

                {/* Document Cards */}
                <View style={styles.documentsContainer}>
                    {isPreviewMode && previewDocumentType ? (
                        // In preview mode, show only the requested document
                        (() => {
                            const labels: Record<DocumentType, { label: string; sideLabel: string }> = {
                                emiratesIdFront: {
                                    label: t('documentViewer.front'),
                                    sideLabel: t('documentViewer.frontSide')
                                },
                                emiratesIdBack: {
                                    label: t('documentViewer.back'),
                                    sideLabel: t('documentViewer.backSide')
                                },
                                passport: {
                                    label: t('documentViewer.passport'),
                                    sideLabel: t('documentViewer.passportSide')
                                }
                            };
                            const docLabels = labels[previewDocumentType];
                            return (
                                <DocumentCard
                                    documentType={previewDocumentType}
                                    label={docLabels.label}
                                    sideLabel={docLabels.sideLabel}
                                />
                            );
                        })()
                    ) : registrationType === REGISTRATION_TYPE.EMIRATES_ID ? (
                        <>
                            <DocumentCard
                                documentType="emiratesIdFront"
                                label={t('documentViewer.front')}
                                sideLabel={t('documentViewer.frontSide')}
                            />
                            <DocumentCard
                                documentType="emiratesIdBack"
                                label={t('documentViewer.back')}
                                sideLabel={t('documentViewer.backSide')}
                            />
                        </>
                    ) : (
                        <DocumentCard
                            documentType="passport"
                            label={t('documentViewer.passport')}
                            sideLabel={t('documentViewer.passportSide')}
                        />
                    )}
                </View>
            </ScrollView>

            {/* Continue Button */}
            {!isPreviewMode && (
                <View style={[styles.continueButtonContainer, { bottom: Math.max(insets.bottom, 45) }]}>
                    <Button
                        variant="primary"
                        title={t('common.continue')}
                        fullWidth={true}
                        onPress={handleContinue}
                        disabled={!areAllRequiredDocumentsCaptured() || isUploading}
                    />
                </View>
            )}

            <OkDialog
                visible={errorVisible}
                message={errorMessage}
                onOk={() => setErrorVisible(false)}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#ffffff',
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        paddingHorizontal: 16,
    },
    pageHeader: {
        flexDirection: 'column',
        gap: 16, // Figma: gap-[var(--spacing\/4,16px)] between icon, title, subtitle
        marginBottom: 20, // Figma: gap-[var(--spacing\/5,20px)] between header and documents
    },
    previewPageHeader: {
        width: '100%',
        maxWidth: PREVIEW_CARD_MAX_WIDTH,
        alignSelf: 'center',
    },
    iconContainer: {
        paddingHorizontal: 8,
    },
    pageTitle: {
        fontSize: 28,
        fontWeight: '500',
        lineHeight: 36,
        textAlign: 'left',
    },
    pageSubtitle: {
        fontSize: 16,
        fontWeight: '400',
        lineHeight: 24,
        textAlign: 'left',
    },
    noteContainer: {
        marginTop: 4,
    },
    noteText: {
        fontSize: 15,
        fontWeight: '400',
        lineHeight: 22,
        textAlign: 'left',
    },
    noteBold: {
        fontSize: 15,
        fontWeight: '600',
    },
    documentsContainer: {
        flexDirection: 'column',
        gap: 16,
        width: '100%',
        alignItems: 'center',
    },
    documentCard: {
        backgroundColor: '#ffffff',
        borderRadius: 16,
        padding: 4,
        gap: 8,
        width: '100%',
        maxWidth: PREVIEW_CARD_MAX_WIDTH,
        alignSelf: 'center',
    },
    previewCard: {
        padding: 8,
    },
    documentImageContainer: {
        width: '100%',
        borderRadius: 16,
        overflow: 'hidden',
        alignItems: 'center', // Center the placeholder/image
        alignSelf: 'center',
    },
    previewImageContainer: {
        maxWidth: PREVIEW_CARD_MAX_WIDTH,
        borderRadius: PREVIEW_CARD_RADIUS,
        overflow: 'hidden',
        width: PREVIEW_CARD_MAX_WIDTH,
    },
    previewImageWrapper: {
        width: '100%',
        maxWidth: PREVIEW_CARD_MAX_WIDTH,
        borderRadius: PREVIEW_CARD_RADIUS,
        overflow: 'hidden',
    },
    imageWrapper: {
        width: '100%',
        maxWidth: PREVIEW_CARD_MAX_WIDTH,
        position: 'relative',
        alignSelf: 'center',
    },
    imageContainer: {
        width: '100%',
        maxWidth: PREVIEW_CARD_MAX_WIDTH,
        aspectRatio: PREVIEW_IMAGE_ASPECT_RATIO,
        position: 'relative',
        borderRadius: 16,
        overflow: 'hidden',
    },
    documentImage: {
        width: '100%',
        height: '100%',
        borderRadius: 16,
    },
    uploadStatusOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
    },
    uploadStatusContent: {
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingHorizontal: 16,
    },
    uploadStatusText: {
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: '500',
    },
    checkmarkCircle: {
        width: 32,
        height: 32,
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
    },
    errorCircle: {
        width: 32,
        height: 32,
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
    },
    retryButton: {
        marginTop: 4,
        paddingHorizontal: 16,
        paddingVertical: 12,
        alignSelf: 'center',
        minHeight: 44,
    },
    imageActions: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        marginTop: 8,
    },
    previewImageActions: {
        justifyContent: 'space-between',
        width: '100%',
        maxWidth: PREVIEW_CARD_MAX_WIDTH,
        alignSelf: 'center',
        paddingHorizontal: 4,
    },
    imageActionButton: {
        paddingHorizontal: 0,
        paddingVertical: 0,
    },
    emptyStateContainer: {
        width: '100%', // Figma: w-[300px] / 361px max in preview
        maxWidth: PREVIEW_CARD_MAX_WIDTH,
        aspectRatio: PREVIEW_IMAGE_ASPECT_RATIO,
        borderRadius: PREVIEW_CARD_RADIUS,
        overflow: 'hidden',
    },
    emptyStateBorder: {
        width: '100%',
        height: '100%',
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: '#CCCCCC',
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
    },
    emptyStateContent: {
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0, // Figma: Elements positioned with specific spacing, not gap
        width: '100%',
        height: '100%',
    },
    placeholderIconContainer: {
        // Figma: Icon is 39.169px, positioned at mt-[25.82px] for front, mt-[31.16px] for back
        marginTop: 26, // Average positioning
        marginBottom: 0,
    },
    emptyStateText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#B2B2B2',
        marginTop: 8, // Spacing between icon and text
        marginBottom: 0,
    },
    uploadButtonInside: {
        marginTop: 8, // Spacing between text and button
        paddingHorizontal: 16, // Increased padding for larger touch area
        paddingVertical: 12, // Increased padding for larger touch area
        alignSelf: 'center', // Override Button's default flex-start to center it
        minHeight: 44, // Minimum touch target size (iOS/Android guidelines)
    },
    documentInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 4, // Figma: px-[4px]
        paddingVertical: 0, // Figma: py-0
        width: '100%',
    },
    documentLabel: {
        fontSize: 16,
        fontWeight: '600',
        // Color handled by Typography component
    },
    editLink: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    editLinkText: {
        textDecorationLine: 'underline',
    },
    actionButton: {
        paddingHorizontal: 0, // Button component handles its own padding
        paddingVertical: 0,
        alignSelf: 'flex-start', // Keep default for edit button (right-aligned in flex row)
    },
    continueButtonContainer: {
        position: 'absolute',
        left: 16,
        right: 16,
    },
});

export default DocumentUploadScreen;
